/**
 * Every Bangla string the eleven state frames draw, transcribed from
 * `design/Oprappo P0 States.dc.html` — and, since W1-04, ONLY the strings that are the
 * same sentence for everybody.
 *
 * ── WHAT USED TO BE HERE, AND WHY IT IS GONE ────────────────────────────────────
 * The frames were drawn for one imagined customer on one imagined afternoon, and this file
 * carried that afternoon into the product: the drawn person's name greeting everybody, an
 * অপ্রাপ্য ক্যাশ figure offered as "যথেষ্ট" to people who hold none, a grocery order nobody
 * placed, two activity rows with order numbers and an electrician's name, a named provider
 * with two rated rivals, a verification that "should finish before 2pm", a support
 * reference, and a health domain that "is coming" in the middle of somebody else's
 * search. Every one of those rendered as fact. None was. (They are not quoted here, on
 * purpose: `__tests__/honest.test.tsx` greps this directory for them, comments included.)
 *
 * They are deleted rather than kept as fixtures, because a fixture in the same file as the
 * copy is one missing prop away from being shown — which is exactly how each of them was
 * shown. What a frame needs to say about THIS person, THIS order or THIS failure now comes
 * from where it is true — the error envelope the server sent, the profile this device
 * last read, the query in the address bar — or is not said at all. The components say
 * which, prop by prop. The deviations from the drawn frames that follow are recorded, row
 * by row, in `docs/deviations/W1-04.md` (founder decision 2026-09-18: no fictitious
 * people, providers, ratings or balances).
 *
 * ── WHAT IS STILL HERE ──────────────────────────────────────────────────────────
 * Headings, labels, button names and rules of the product that hold for every person:
 * transcribed, `copy_status: 'sourced'`, as before. One line is not transcribed and is
 * marked where it sits (`LOADING.progress_bn`).
 *
 * TWO STATES HAVE NO COPY HERE AT ALL
 *
 * `সেবা সাময়িক বন্ধ` and `অনুমোদন অপেক্ষমাণ` are rows in the applicability matrix and
 * chips in `Architecture § 15`, but NO FRAME IS DRAWN FOR EITHER, anywhere in the
 * design corpus. Their components therefore render server copy only — `message_bn`
 * from the error body — and the sole Bangla they carry is the matrix row label, which
 * is itself a drawn string.
 */

/* ═════════════════════════════════════ § ১ · নতুন ব্যবহারকারীর হোম · Empty · Home ═══ */

/**
 * The name under the greeting is the person's own (`auth/session.ts § cachedDisplayName`)
 * or absent. The drawn "সবচেয়ে বেশি যা করা হয়" list is gone with it: "most done" is a
 * count of what customers do, and a product with no history has no such count.
 */
export const NEW_USER_HOME = {
  greeting_bn: 'আসসালামু আলাইকুম',
  search_placeholder_bn: 'কী প্রয়োজন?',
  /** Four, not eight: "a first-time user has no history to rank them by." */
  quick_actions: [
    { id: 'recharge', label_bn: 'রিচার্জ' },
    { id: 'bill', label_bn: 'বিল পে' },
    { id: 'food', label_bn: 'খাবার' },
    { id: 'ride', label_bn: 'রাইড' },
  ],
  card_title_bn: 'প্রথমবার এসেছেন',
  card_body_bn:
    'উপরে যা প্রয়োজন লিখুন, বা নিচের যেকোনো একটি দিয়ে শুরু করুন। একবার ব্যবহার করলে পরের বার এক ট্যাপেই হবে।',
} as const;

/* ══════════════════════════════════ § ২ · কোনো অর্ডার নেই · Empty · Activity ════════ */

/** Replaced by `GET /v1/activities` returning an empty page. */
export const NEW_USER_ACTIVITY = {
  title_bn: 'অ্যাক্টিভিটি',
  tabs: [
    { id: 'all', label_bn: 'সব' },
    { id: 'active', label_bn: 'চলমান' },
    { id: 'done', label_bn: 'সম্পন্ন' },
  ],
  empty_title_bn: 'এখনও কিছু নেই',
  empty_body_bn: 'অর্ডার, বুকিং, বিল — যা কিছু করবেন সব এখানে জমা থাকবে। রসিদ আর অবস্থা এক জায়গায়।',
  empty_action_bn: 'কিছু খুঁজে দেখুন',
} as const;

/**
 * The way out a frame offers when the server named none: open the one search door.
 * § ২'s own button, transcribed — the one drawn sentence in these frames that sends a
 * person somewhere useful without claiming anything about them.
 */
export const SEARCH_ACTION_BN = NEW_USER_ACTIVITY.empty_action_bn;

/* ══════════════════════════════════════ § ৩ · লোডিং — স্কেলিটন · Loading · Results ══ */

export const LOADING = {
  /**
   * AUTHORED, not transcribed — `docs/copy-review/W1-04.md`. The frame draws
   * "কাছাকাছি টেকনিশিয়ান খোঁজা হচ্ছে…", the line for its own drawn query (এসি মেরামত), and
   * `routes/search` shows this frame for EVERY query: a search for rice announced that it
   * was finding technicians. The drawn query is gone too; the field carries the person's
   * own, from the address bar, or stays empty.
   */
  progress_bn: 'খোঁজা হচ্ছে…',
  footer_bn: 'ধীর সংযোগে ১০ সেকেন্ড পর্যন্ত লাগতে পারে।',
} as const;

/* ══════════════════════════════ § ৪ · কোনো এআই ইনসাইট নেই · Empty · AI ═════════════ */

/**
 * The header and the composer. The drawn conversation between them — a question about a
 * horse doctor in Uttara, an answer, two offers and a demand line — was one imagined
 * exchange, and an empty AI screen for a new person has no exchange in it yet.
 */
export const NEW_USER_AI = {
  title_bn: 'অপ্রাপ্য এআই',
  composer_placeholder_bn: 'আরও কিছু বলুন…',
} as const;

/* ═════════════════════════════════════ § ৫ · পেমেন্ট ব্যর্থ · Failure · Payment ════ */

/**
 * ORDER IS STILL LOAD-BEARING. The design's own note: "'আপনার টাকা কাটা হয়নি' is the
 * second line, in green, before any explanation." That line is now the SERVER's
 * (`ApiErrorBody.money_position_bn`), because only the server knows it: a duplicate
 * transaction id is money that DID move. `PaymentFailedState` still renders title, money,
 * cause in that order and offers no prop that could reorder them.
 *
 * Gone: the drawn cause (a bank refusing a card — card payments are not offered), the
 * order block (an order nobody placed), and the three ways forward — অপ্রাপ্য ক্যাশ (the
 * payment method is withdrawn, founder decision 2026-09-18, and the figure under it
 * was invented), a card retry (no card rail), and হাতে হাতে (whether an order can be paid
 * at the door is the order's fact, not the frame's). What is left is the way forward that
 * is always true: try again. `retry_bn` is § ৬'s drawn button.
 */
export const PAYMENT_FAILED = {
  header_bn: 'পেমেন্ট',
  title_bn: 'পেমেন্ট হয়নি',
  next_label_bn: 'এখন কী করবেন',
  retry_bn: 'আবার চেষ্টা করুন',
  support_bn: 'সমস্যা থাকলে সহায়তা নিন',
} as const;

/* ═════════════════════════════════════════ § ৬ · ইন্টারনেট নেই · Failure · Offline ══ */

/**
 * Gone: the name (now the person's own or none), the banner's second sentence and the
 * "অফলাইনেও যা দেখা যাবে" list. The service worker never stores an API answer
 * (`src/sw.ts § /v1/** NEVER CACHED`), so offline there is no live order, no saved
 * receipt and no saved technician's number to open — the list promised three things the
 * app cannot show, and "যা আগে দেখেছেন তা নিচে আছে" pointed at it.
 */
export const OFFLINE = {
  greeting_bn: 'আসসালামু আলাইকুম',
  search_disabled_bn: 'সার্চ করতে ইন্টারনেট লাগবে',
  banner_title_bn: 'ইন্টারনেট নেই',
  blocked_bn: 'নতুন অর্ডার দিতে সংযোগ দরকার।',
  retry_bn: 'আবার চেষ্টা করুন',
} as const;

/* ══════════════════════════════════════════════ § ৭ · সিঙ্ক ত্রুটি · Failure · Sync ══ */

/**
 * `stale_label_bn` IS NOT COMPOSED. The drawn label is `১১:৪০-এর তথ্য` — Bangla text
 * with Bangla numerals — and docs/05 forbids the client composing Bangla from
 * fragments, so it cannot be built from `ActivityRow.stale_at`. It arrives per row, from
 * whoever holds the rows; one label per row, never one banner.
 *
 * Gone: the two drawn rows (invented orders) and the drawn banner sentence, which names
 * a time — "শেষ আপডেট ১১:৪০" — that no failure this frame is shown for has. The banner now
 * carries the server's own sentence, or the state's drawn name. The note under the rows
 * is kept for when there ARE rows; with none, "what you are seeing may be old" is about
 * nothing.
 */
export const SYNC_ERROR = {
  title_bn: 'অ্যাক্টিভিটি',
  tabs: [
    { id: 'all', label_bn: 'সব' },
    { id: 'active', label_bn: 'চলমান' },
    { id: 'done', label_bn: 'সম্পন্ন' },
  ],
  banner_action_bn: 'আবার',
  note_title_bn: 'যা দেখছেন তা পুরনো হতে পারে',
  note_body_bn: 'অর্ডারের অবস্থা বদলে থাকতে পারে। নিশ্চিত হতে টেকনিশিয়ানকে সরাসরি কল করুন।',
  support_bn: 'সমস্যা থাকলে সহায়তা নিন',
} as const;

/* ═══════════════════════════════ § ৮ · প্রদানকারী অনুপলব্ধ · Unavailable · Provider ══ */

/**
 * Nothing about a provider is kept. The frame is a provider's page — a name, an area, a
 * "closed today" chip, the next free slot and two rated alternatives — and every one of
 * those is a fact about a particular provider that only the server can state. The
 * founder decision of 2026-09-18 is explicit that no fictitious provider or rating is
 * shown. What remains is the state's own name and the server's sentence
 * (`ProviderUnavailableState`), and the search door as the way on.
 */
export const PROVIDER_UNAVAILABLE = {
  search_action_bn: SEARCH_ACTION_BN,
} as const;

/* ═══════════════════════════════════ § ৯ · যাচাই অপেক্ষমাণ · Pending · Verification ══ */

/**
 * The heading and the chip. Gone: the header (অপ্রাপ্য ক্যাশ — this frame is now what
 * a deferred session meets when it tries to ORDER, founder decision 2026-09-18), the
 * drawn duration ("আজ দুপুর ২টার আগে"), the three-step document rail with its timestamp,
 * the "meanwhile" list — which promised ordering by card or by hand and all bills and
 * recharge to a session that can do none of them — and the promise of a notification.
 * How long, and what still works, are the server's to say in `message_bn`.
 */
export const VERIFICATION_PENDING = {
  title_bn: 'পরিচয় যাচাই চলছে',
  chip_bn: 'অপেক্ষমাণ',
} as const;

/* ═════════════════════════════════════════════ § ১০ · অনুমতি নেই · Permission denied ══ */

/**
 * The generic refusal line only. The drawn frame is one particular refusal — a finance
 * person, in ফাউন্ডার মোড, at অপ্রাপ্য লিমিটেড, turned away from the cap table — and every
 * line of it but the title named that case: the header, the organisation, the body, the
 * four things "you can see", the request that "will be logged", and "back to the finance
 * dashboard". Shown to anybody else, each was false. Where the refusal happened and what
 * it concerns now come from the refusal (`header_bn`, `title_bn`, `body_bn`,
 * `message_bn`); the request button returns when there is an endpoint that records a
 * request.
 *
 * `ফাউন্ডার` throughout, never `মালিক` (docs/02 § terminology) — which now means only
 * that the frame no longer says either word unless the server does.
 */
export const PERMISSION_DENIED = {
  title_bn: 'এই তথ্য আপনার ভূমিকায় দেখা যায় না',
} as const;

/* ═══════════════════════════════════════════ § ১১ · সেবাটি আসছে · Not yet live ══════ */

/**
 * The alternatives heading and the notify label. Gone: the drawn query and the health
 * copy (every unlaunched domain was announced as "স্বাস্থ্য সেবা"), the two drawn
 * alternatives, the demand line (a claim that a database row was written) and the
 * coverage footer — "পাঁচটি ক্ষেত্র এখন চালু · সাতটি আসছে", no longer true of a product
 * with all twelve domains live (founder decision 2026-09-18). Alternatives render only
 * when the server sends them (`DomainNotLiveError.alternatives`); the notify control only
 * when there is something that records the request.
 */
export const DOMAIN_NOT_LIVE = {
  notify_bn: 'চালু হলে জানান',
  alternatives_label_bn: 'এখন যা করতে পারেন',
} as const;
